"use client";

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { deepMerge } from "@/lib/content";

type PageContentResponse<T> = {
  page: string;
  data: T | null;
  updated_at: string | null;
};

/**
 * Returns admin-editable content for a public page, deep-merged over the
 * in-code defaults. The default is used for the first render (and during
 * prerender) so the page never flashes empty and works before the DB table
 * exists. Content is fetched client-side after mount.
 */
export function usePageContent<T>(page: string, fallback: T): T {
  const [content, setContent] = useState<T>(fallback);

  useEffect(() => {
    let active = true;
    api
      .getPageContent<Partial<T>>(page)
      .then((res: PageContentResponse<Partial<T>>) => {
        if (active && res?.data) {
          setContent(deepMerge(fallback, res.data) as T);
        }
      })
      .catch(() => {
        // Network/table missing — keep the in-code defaults.
      });
    return () => {
      active = false;
    };
    // `fallback` is a stable module constant per page; refetch only on page change.
  }, [page]);

  return content;
}
